import app from 'flarum/forum/app';
import Modal from 'flarum/common/components/Modal';
import Button from 'flarum/common/components/Button';
import LoadingIndicator from 'flarum/common/components/LoadingIndicator';
import { formatText, looksLikeUrl } from '../utils/keyText';
/**
 * Interactive dichotomous-key stepper - "à la volée" rendering of a .cle
 * file's structure (see CleKeyParser.php / DeterminationKeyContentResource)
 * as an actual step-through tool, not just a downloadable blob. Matches
 * keys.champis.net's own "AFFICHER" feature in substance: same underlying
 * step/choice/conclusion structure, presented as click-through navigation
 * (their own viewer renders the whole key as one long static page instead -
 * this is a deliberate improvement, not a fidelity gap, since the raw
 * format is genuinely a step tree and stepping through it one choice at a
 * time is a more usable match for "vraies clés de détermination" than a
 * wall of text).
 *
 * this.history is a stack of {section, step} pairs - "Retour" pops it
 * rather than re-deriving a path, since a choice's target doesn't carry
 * where it came from.
 */
export default class DeterminationKeyViewer extends Modal {
  data = null;
  error = false;
  currentSection = null;
  currentStep = null;
  history = [];
  oninit(vnode) {
    super.oninit(vnode);
    this.data = null;
    this.error = false;
    this.currentSection = null;
    this.currentStep = null;
    this.history = [];
    this.load();
  }
  className() {
    return 'Modal--large DeterminationKeyViewer';
  }
  title() {
    return this.data && this.data.title || this.attrs.fileName;
  }
  load() {
    app.request({
      method: 'GET',
      url: app.forum.attribute('apiUrl') + '/determination-key-content',
      params: {
        filter: {
          uuid: this.attrs.uuid
        }
      }
    }).then(response => {
      const item = response.data && response.data[0];
      if (!item) {
        this.error = true;
      } else {
        this.data = item.attributes;
        this.currentSection = this.data.startSection;
        this.currentStep = '1';
      }
      m.redraw();
    }).catch(() => {
      this.error = true;
      m.redraw();
    });
  }
  content() {
    if (this.error) {
      return m("div", {
        className: "Modal-body"
      }, m("p", {
        className: "DeterminationKeyViewer-error"
      }, app.translator.trans('champis-keys.forum.viewer.error')));
    }
    if (!this.data) {
      return m("div", {
        className: "Modal-body"
      }, m(LoadingIndicator, null));
    }
    const section = this.data.sections[this.currentSection];
    const choices = section && section.steps && section.steps[this.currentStep];
    if (!choices) {
      return m("div", {
        className: "Modal-body"
      }, m("p", {
        className: "DeterminationKeyViewer-error"
      }, app.translator.trans('champis-keys.forum.viewer.error')));
    }
    return m("div", {
      className: "Modal-body DeterminationKeyViewer"
    }, section.title && this.currentStep === '1' && m("p", {
      className: "DeterminationKeyViewer-sectionTitle"
    }, section.title), this.history.length > 0 && m(Button, {
      className: "Button DeterminationKeyViewer-back",
      icon: "fas fa-arrow-left",
      onclick: () => this.goBack()
    }, app.translator.trans('champis-keys.forum.viewer.back_button')), m("ul", {
      className: "DeterminationKeyViewer-choices"
    }, choices.map((choice, index) => this.renderChoice(choice, index))));
  }
  renderChoice(choice, index) {
    const target = choice.target;
    const isConclusion = target.type === 'conclusion';
    // "info": a plain descriptive block (genus description, a footnote
    // reached via a "Note de l'auteur"-style reference) - its own text is
    // the whole content, there's nowhere further to go and no separate
    // result to show alongside it (see CleKeyParser's resolveTarget()).
    const isInfo = target.type === 'info';
    // "link": an internet link, or a companion file bundled in the same
    // .zip (DeterminationKeyContentResource resolves the latter to a real
    // URL server-side - see CleKeyParser's own docblock for the format's
    // f"..." syntax both share). A standalone .cle can reference a
    // companion file with nothing to resolve it from - target.external is
    // only ever set for a real internet link, so anything else needs
    // looksLikeUrl's own check before rendering a (real, working) link
    // rather than a dead one.
    const isLink = target.type === 'link' && (target.external || looksLikeUrl(target.value));
    const isTerminal = isConclusion || isInfo || isLink;
    return m("li", {
      key: index,
      className: 'DeterminationKeyViewer-choice' + (isTerminal ? ' DeterminationKeyViewer-choice--conclusion' : ''),
      onclick: !isTerminal ? () => this.navigate(target) : undefined
    }, m("span", {
      className: "DeterminationKeyViewer-choiceText"
    }, choice.letter && m("strong", null, choice.letter, ". "), formatText(choice.text)), choice.note && m("div", {
      className: "DeterminationKeyViewer-note"
    }, formatText(choice.note)), isConclusion && m("div", {
      className: "DeterminationKeyViewer-result"
    }, m("i", {
      className: "icon fas fa-flag-checkered",
      "aria-hidden": "true"
    }), " ", target.value), isLink && m("a", {
      href: target.value,
      target: "_blank",
      rel: "noopener noreferrer",
      className: "DeterminationKeyViewer-result DeterminationKeyViewer-link"
    }, m("i", {
      className: 'icon fas ' + (target.external ? 'fa-arrow-up-right-from-square' : 'fa-file-arrow-down'),
      "aria-hidden": "true"
    }), ' ', app.translator.trans(target.external ? 'champis-keys.forum.viewer.open_link' : 'champis-keys.forum.viewer.open_file')), !isTerminal && m("i", {
      className: "icon fas fa-chevron-right DeterminationKeyViewer-arrow",
      "aria-hidden": "true"
    }));
  }
  navigate(target) {
    this.history.push({
      section: this.currentSection,
      step: this.currentStep
    });
    if (target.type === 'section') {
      this.currentSection = target.value;
      this.currentStep = '1';
    } else {
      this.currentStep = target.value;
    }
  }
  goBack() {
    const previous = this.history.pop();
    this.currentSection = previous.section;
    this.currentStep = previous.step;
  }
}
flarum.reg.add('champis-keys', 'forum/components/DeterminationKeyViewer', DeterminationKeyViewer);